/**
 * Client-side agent-spec validation: the JSON Schema from platform/sdk/schema (pre-compiled at
 * build time by scripts/sync-schema.mjs) plus the semantic rules of
 * agent_platform_sdk.spec.model.semantic_errors that matter in the builder. The server-side SDK
 * validation remains authoritative; this gives authors immediate feedback.
 */
import { runningKind } from "./team";
import schemaValidate from "../schema/agent-spec.validate.js";
import { decisionOptions, routesByOption } from "./editor";
import { specEdges } from "./graph";
import { levelIssues } from "./approvalChain";
import type { AgentSpec, ApprovalLevel } from "./types";

export interface SpecIssue {
  path: string;
  message: string;
  source: "schema" | "semantic" | "nocode";
}

interface AjvError {
  instancePath: string;
  message?: string;
  keyword: string;
  params: Record<string, unknown>;
}
type StandaloneValidate = ((data: unknown) => boolean) & { errors?: AjvError[] | null };

export function schemaIssues(doc: unknown): SpecIssue[] {
  const v = schemaValidate as unknown as StandaloneValidate;
  if (v(doc)) return [];
  // oneOf over node types reports one error per branch; keep the most useful ones.
  const errs = (v.errors ?? []).filter((e) => e.keyword !== "oneOf" && e.keyword !== "if");
  const seen = new Set<string>();
  const out: SpecIssue[] = [];
  for (const e of errs) {
    const extra = e.keyword === "additionalProperties" ? ` (${String(e.params.additionalProperty)})` : "";
    const msg = `${e.message ?? e.keyword}${extra}`;
    const key = `${e.instancePath}|${msg}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ path: e.instancePath || "/", message: msg, source: "schema" });
  }
  return out;
}

/** As agent_platform_sdk.spec.model._Node: no hyphens, at most 41 characters. */
const ID = /^[a-z][a-z0-9_]{0,40}$/;
/** The orchestrator only starts `name@x.y.z` refs matching these (StartRun.agent). */
const AGENT_NAME = /^[a-z][a-z0-9-]{2,40}$/;
/** As the orchestrator's BOUNDARY node types (compiler.py). */
const BOUNDARY = new Set(["tool", "human_approval", "human_input", "subgraph", "retrieval", "wait"]);
const SEMVER = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/;

export function semanticIssues(doc: AgentSpec): SpecIssue[] {
  const out: SpecIssue[] = [];
  if (!AGENT_NAME.test(doc.metadata.name)) {
    out.push({ path: "/metadata/name", message: "name must be 3–41 lowercase letters, digits or hyphens, starting with a letter", source: "semantic" });
  }
  if (!SEMVER.test(doc.metadata.version)) {
    out.push({ path: "/metadata/version", message: "version must be semantic (x.y.z)", source: "semantic" });
  }
  const s = doc.spec;
  const g = s.graph;
  const ids = g.nodes.map((n) => n.id);
  const known = new Set(ids);
  const dupes = [...new Set(ids.filter((id, i) => ids.indexOf(id) !== i))];
  if (dupes.length) out.push({ path: "/spec/graph/nodes", message: `duplicate node ids: ${dupes.join(", ")}`, source: "semantic" });
  if (!known.has(g.entry)) out.push({ path: "/spec/graph/entry", message: `entry ${g.entry} is not a node`, source: "semantic" });
  for (const id of ids) {
    if (!ID.test(id)) out.push({ path: "/spec/graph/nodes", message: `node id ${id} must be lowercase letters, digits or _`, source: "semantic" });
  }
  for (const [src, dst] of specEdges(doc)) {
    for (const end of [src, dst]) {
      if (!known.has(end)) out.push({ path: "/spec/graph/edges", message: `edge ${src}->${dst} references unknown node ${end}`, source: "semantic" });
    }
  }
  const byId = new Map(g.nodes.map((n) => [n.id, n]));
  for (const e of g.edges ?? []) {
    const src = byId.get(e.from);
    if (e.when && src && routesByOption(src)) {
      const values = decisionOptions(src).map((o) => o.value);
      if (!values.includes(e.when)) {
        out.push({ path: "/spec/graph/edges", message: `edge ${e.from}->${e.to}: ${e.when} is not an option of decision ${src.id}`, source: "semantic" });
      }
    }
  }
  const granted = new Map((s.tools ?? []).map((t) => [t.ref, new Set(t.actions)]));
  for (const n of g.nodes) {
    if (n.type === "tool" && !granted.get(n.tool ?? "")?.has(n.action ?? "")) {
      out.push({ path: "/spec/graph/nodes", message: `tool node ${n.id} uses ${n.tool}.${n.action} not granted in spec.tools`, source: "semantic" });
    }
    if (n.type === "llm") {
      for (const t of n.tools ?? []) {
        if (!granted.has(t)) out.push({ path: "/spec/graph/nodes", message: `llm node ${n.id} offers tool ${t} not granted`, source: "semantic" });
      }
    }
    if (n.type === "llm") {
      // As agent_platform_sdk.spec.model.PromptRef: exactly one of a saved prompt or instructions.
      const pr = (n.prompt ?? {}) as { ref?: string | null; template?: string | null };
      const hasRef = !!pr.ref;
      const hasTemplate = pr.template !== undefined && pr.template !== null;
      if (hasRef && hasTemplate) out.push({ path: "/spec/graph/nodes", message: `llm node ${n.id} has both a saved prompt and instructions`, source: "semantic" });
      else if (!hasRef && !hasTemplate) out.push({ path: "/spec/graph/nodes", message: `llm node ${n.id} has neither a saved prompt nor instructions`, source: "semantic" });
    }
    if (n.type === "human_approval" && !(typeof n.subject_key === "string" && n.subject_key.trim())) {
      out.push({ path: `/spec/graph/nodes/${g.nodes.indexOf(n)}`, message: "must have required property 'subject_key'", source: "semantic" });
    }
    if (n.type === "parallel") {
      // As the orchestrator: system, person, knowledge and sub-agent steps run as their own
      // activities, AI and code steps inside one; a group can't mix the two.
      const outside = new Set((n.branches ?? []).map((b) => byId.get(b)).filter((b) => b !== undefined).map((b) => BOUNDARY.has(b.type)));
      if (outside.size > 1) out.push({ path: "/spec/graph/nodes", message: `parallel ${n.id} mixes system, person or knowledge steps with AI or code steps`, source: "semantic" });
    }
    // As the SDK: "if the limit is reached, go to" needs a repeat limit and another step.
    if (n.on_limit) {
      if (n.on_limit === n.id) out.push({ path: "/spec/graph/nodes", message: `step ${n.id} on_limit cannot go back to the same step`, source: "semantic" });
      if (!n.max_iterations) out.push({ path: "/spec/graph/nodes", message: `step ${n.id} has on_limit but no max_iterations`, source: "semantic" });
    }
    // As the SDK (ADR-0079): a system step's failure path goes to another step.
    if (n.type === "tool" && typeof n.on_error === "string" && n.on_error === n.id) {
      out.push({ path: "/spec/graph/nodes", message: `tool node ${n.id} on_error cannot go back to the same step`, source: "semantic" });
    }
    // As the SDK (ADR-0094): a "Wait for" step waits for something, and its give-up path exists.
    if (n.type === "wait") {
      const returns = g.nodes.some((x) => x.type === "llm" && JSON.stringify(x.reply ?? {}).includes(`"wait":"${n.id}"`));
      if (!(n.event || n.answer || n.after || n.at || returns)) out.push({ path: "/spec/graph/nodes", message: `wait ${n.id} waits for nothing`, source: "semantic" });
      if (n.event && !(n.event.name ?? "").trim()) out.push({ path: "/spec/graph/nodes", message: `wait ${n.id} needs an event name`, source: "semantic" });
      if (n.event && !(n.event.key ?? "").trim()) out.push({ path: "/spec/graph/nodes", message: `wait ${n.id} needs a value to match events on`, source: "semantic" });
      if (n.at !== undefined && n.at !== null && !String(n.at).trim()) out.push({ path: "/spec/graph/nodes", message: `wait ${n.id} needs a date and time`, source: "semantic" });
      if (n.on_give_up === n.id) out.push({ path: "/spec/graph/nodes", message: `wait ${n.id} on_give_up cannot go back to the same step`, source: "semantic" });
    }
    // As the SDK (ADR-0104): a for-each step has a list and something to do for each item, and
    // its steps keep to themselves.
    if (n.type === "foreach") {
      if (!(n.items ?? "").trim()) out.push({ path: "/spec/graph/nodes", message: `foreach ${n.id} needs a list`, source: "semantic" });
      const body = new Set(n.steps ?? []);
      if (!body.size && !n.agent) out.push({ path: "/spec/graph/nodes", message: `foreach ${n.id} needs steps or an agent`, source: "semantic" });
      for (const e of g.edges ?? []) if (body.has(e.from) !== body.has(e.to)) out.push({ path: "/spec/graph/edges", message: `foreach ${n.id}: the arrow ${e.from}->${e.to} leaves or enters its steps`, source: "semantic" });
      for (const s of body) if (!known.has(s)) out.push({ path: "/spec/graph/nodes", message: `foreach ${n.id} uses unknown step ${s}`, source: "semantic" });
    }
    // As the SDK (ADR-0105): matching needs two tables and a column; grouping the rows and how.
    if (n.type === "table") {
      if (n.op === "match" && !(n.left && n.right && (n.keys ?? []).length)) out.push({ path: "/spec/graph/nodes", message: `table ${n.id} needs two tables and a column to match on`, source: "semantic" });
      if (n.op === "group" && !(n.source && ((n.by ?? []).length || (n.measures ?? []).length))) out.push({ path: "/spec/graph/nodes", message: `table ${n.id} needs rows and what to group by`, source: "semantic" });
    }
    // As the SDK (ADR-0103): a Calculate step works each value out once, with a formula.
    if (n.type === "calculate") {
      const keys = (n.values ?? []).map((c) => c.key);
      const twice = [...new Set(keys.filter((k, i) => keys.indexOf(k) !== i))];
      if (twice.length) out.push({ path: "/spec/graph/nodes", message: `calculate ${n.id} works out ${twice.join(", ")} more than once`, source: "semantic" });
      for (const c of n.values ?? []) {
        if (!c.expr.trim()) out.push({ path: "/spec/graph/nodes", message: `calculate ${n.id} value ${c.key} has no formula`, source: "semantic" });
        else if (!balanced(c.expr)) out.push({ path: "/spec/graph/nodes", message: `calculate ${n.id} value ${c.key} has an unfinished formula`, source: "semantic" });
      }
    }
    if (n.type === "retrieval" && !(s.knowledge ?? []).some((k) => k.collection === n.collection)) {
      out.push({ path: "/spec/graph/nodes", message: `retrieval node ${n.id} uses undeclared collection ${n.collection}`, source: "semantic" });
    }
  }
  for (const rule of s.approvals ?? []) {
    if (!granted.get(rule.tool)?.has(rule.action)) {
      out.push({ path: "/spec/approvals", message: `approval rule targets ${rule.tool}.${rule.action} not granted`, source: "semantic" });
    }
    // As the SDK (ADR-0102): each level says who signs it.
    for (const i of levelIssues(rule.levels ?? [])) {
      out.push({ path: "/spec/approvals", message: `approval rule ${rule.tool}.${rule.action} level ${i.index + 1}: ${i.code}`, source: "semantic" });
    }
  }
  for (const n of g.nodes) {
    if (n.type !== "human_approval") continue;
    for (const i of levelIssues((n.levels as ApprovalLevel[] | undefined) ?? [])) {
      out.push({ path: "/spec/graph/nodes", message: `approval ${n.id} level ${i.index + 1}: ${i.code}`, source: "semantic" });
    }
  }
  if (out.length === 0) {
    const succ = new Map<string, string[]>();
    for (const [a, b] of specEdges(doc)) succ.set(a, [...(succ.get(a) ?? []), b]);
    const seen = new Set<string>();
    const stack = [g.entry];
    while (stack.length) {
      const cur = stack.pop() as string;
      if (seen.has(cur)) continue;
      seen.add(cur);
      stack.push(...(succ.get(cur) ?? []));
    }
    const unreachable = ids.filter((i) => !seen.has(i));
    if (unreachable.length) out.push({ path: "/spec/graph", message: `unreachable nodes: ${unreachable.join(", ")}`, source: "semantic" });
  }
  if (s.permissions?.autonomous && s.risk_tier >= 3) {
    out.push({ path: "/spec/permissions/autonomous", message: "autonomous agents are limited to risk tier 2", source: "semantic" });
  }
  return out;
}

/** Brackets and quotes of a formula are closed (the control plane checks the rest). */
function balanced(expr: string): boolean {
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < expr.length; i++) {
    const c = expr[i];
    if (quote) {
      if (c === "\\") i++;
      else if (c === quote) quote = null;
      continue;
    }
    if (c === '"' || c === "'") quote = c;
    else if (c === "(" || c === "[" || c === "{") depth++;
    else if (c === ")" || c === "]" || c === "}") depth--;
    if (depth < 0) return false;
  }
  return depth === 0 && quote === null;
}

export interface NoCodeContext {
  /** Tool refs that are certified in the registry. */
  certifiedTools: ReadonlySet<string>;
}

export function noCodeIssues(doc: AgentSpec, ctx: NoCodeContext): SpecIssue[] {
  const out: SpecIssue[] = [];
  const s = doc.spec;
  if (s.builder_profile !== "nocode") out.push({ path: "/spec/builder_profile", message: "must be nocode", source: "nocode" });
  if (s.risk_tier > 2) out.push({ path: "/spec/risk_tier", message: "no-code agents are limited to risk tier 2", source: "nocode" });
  if (s.graph.nodes.some((n) => n.type === "python")) out.push({ path: "/spec/graph", message: "no-code agents cannot contain python nodes", source: "nocode" });
  // Supervisor and handoff teams are open to guided agents (ADR-0093); old debate teams are not.
  if (s.topology && !runningKind(s.topology)) out.push({ path: "/spec/topology", message: "no-code agents cannot define multi-agent topologies", source: "nocode" });
  // A guided agent works on its own only to serve its schedule or event (ADR-0086).
  if (s.permissions?.autonomous && !(s.triggers ?? []).some((t) => (t as { type?: string }).type !== "manual"))
    out.push({ path: "/spec/permissions/autonomous", message: "no-code agents run on their own only when a schedule or an event starts them", source: "nocode" });
  for (const t of s.tools ?? []) {
    if (!ctx.certifiedTools.has(t.ref)) out.push({ path: "/spec/tools", message: `tool ${t.ref} is not certified`, source: "nocode" });
  }
  return out;
}

/** Full validation; schema errors short-circuit semantic checks (they assume a well-formed doc). */
export function validateSpec(doc: unknown, nocode?: NoCodeContext): SpecIssue[] {
  const schema = schemaIssues(doc);
  if (schema.length) return schema;
  const spec = doc as AgentSpec;
  return [...semanticIssues(spec), ...(nocode ? noCodeIssues(spec, nocode) : [])];
}
